<template>
  <div class="editPrompt flex flex-col mt-4 mb-6 relative overflow-hidden">
    <div class="flex justify-between mb-4">
      <h1 class="text-left text-lg font-semibold text-primary-500 dark:text-primary-200">
        Update On Hand Value
      </h1>
    </div>

    <!-- ADDITION/CONSUMPTION EDIT -->
    <div
      class="w-full flex flex-col justify-center mb-4 transform transition-all duration-500 ease-out"
      :class="stage === 1 && '-translate-x-full opacity-0'"
      :aria-hidden="stage === 1"
      :tabindex="stage === 1 ? -1 : null"
    >
      <div class="flex gap-1 flex-col items-center justify-start mt-2 mb-4">
        <div class="z-0 w-full flex justify-center items-center relative h-8 uppercase tracking-wider">
          <p class="absolute text-green-700 dark:text-green-300 dark:font-bold italic transform transition-all" :class="addedValue <= 0 && 'opacity-0 -translate-y-full'">
            Add
          </p>
          <p class="absolute text-red-700 dark:text-red-400 dark:font-bold italic transform transition-all" :class="addedValue >= 0 && 'opacity-0 translate-y-full'">
            Consume
          </p>
        </div>
        <div
          ref="container"
          class="z-10 flex rounded-xl p-1.5 border border-gray-200 bg-opacity-50 transition-all relative overflow-hidden"
          :class="[addedValue < 0 ? 'bg-red-50 dark:bg-red-900/20 dark:border-red-500/60' : (addedValue > 0 ? 'bg-green-50 dark:bg-green-900/30 dark:border-green-500/60' : 'bg-gray-50 dark:bg-black dark:border-gray-700'), shadowClass]"
        >
          <div
            :style="rippleStyle"
            class="absolute rounded-full pointer-events-none transition-all duration-300 ease-out"
          />
          <button
            @click="decrementAdd"
            :disabled="stage === 1"
            class="mx-4 text-primary-700 dark:text-primary-100 hover:text-primary-500 active:text-primary-300 dark:hover:text-primary-50 dark:active:text-primary-50 relative z-10"
          >
            <font-awesome-icon icon="fa-solid fa-minus" class="h-5 w-5 flex-shrink-0" />
          </button>
          <NumberInput
            v-model="addedValue"
            @update:model-value="setValue"
            :disabled="stage === 1"
            step="0.001"
            placeholder="Consume/Add"
            outlined
            no-arrows
            class="w-34 z-10 relative"
          />
          <button
            @click="incrementAdd"
            :disabled="stage === 1"
            class="mx-4 text-primary-700 dark:text-primary-100 hover:text-primary-500 active:text-primary-300 dark:hover:text-primary-50 dark:active:text-primary-50 z-10 relative"
          >
            <font-awesome-icon icon="fa-solid fa-plus" class="h-5 w-5 flex-shrink-0" />
          </button>
        </div>
      </div>
      <!-- CHANGE -->
      <p class="text-sm">
        {{ displayNum(convertedOnHandVal) }}
        <component
          v-for="part in selUnitAbbreviation"
          :is="part.type ? part.type : 'span'"
          :key="part.key"
        >
          {{ part.content }}
        </component>
        <font-awesome-icon icon="fa-solid fa-arrow-right-long" class="h-3 w-3 flex-shrink-0 text-gray-400 mx-1.5" />
        {{ displayNum(convertedTotalVal) }}
        <component
          v-for="part in selUnitAbbreviation"
          :is="part.type ? part.type : 'span'"
          :key="part.key"
        >
          {{ part.content }}
        </component>
      </p>
      <!-- POSSIBLE UNITS -->
      <p class="w-full text-center lg:text-left text-xs text-gray-500 dark:text-gray-400 mt-8 mb-2">
        Select the units in which you want to add or consume inventory by
      </p>
      <section class="flex justify-center gap-2">
        <button
          v-for="(unit, type) of availableUnits"
          :key="type"
          @click="selectUnit(type)"
          :disabled="stage === 1"
          class="flex flex-col flex-wrap justify-start px-2 py-1 text-sm rounded border transition-all"
          :class="type === selUnit
            ? 'bg-primary-5/30 border-primary-500 dark:bg-primarydark-400/20 dark:border-primarydark-400/40 dark:hover:bg-primarydark/40'
            : 'bg-white border-gray-300 dark:bg-input-dark dark:hover:bg-input-darkhovered dark:border-gray-800'
          "
        >
          <p class="font-medium" :class="type === selUnit && 'text-primary-800 dark:text-primary-200'">
            <component
              v-for="part in displayAbbr(getUnits[unit.id].abbreviation)"
              :is="part.type ? part.type : 'span'"
              :key="part.key"
            >
              {{ part.content }}
            </component>
          </p>
          <p class="uppercase text-xs text-left mt-1" :class="type === selUnit ? 'text-primary-700 dark:text-primary-300' : 'text-gray-500 dark:text-gray-400'">
            {{ type.replace("_", " ") }} Unit
          </p>
        </button>
      </section>
      <!-- UNAVAILABLE UNITS -->
      <p
        v-if="unavailableUnitsStr"
        class="w-11/12 sm:w-9/12 md:w-7/12 lg:w-80 text-left text-sm text-danger font-medium mt-4"
      >
        <font-awesome-icon
          icon="fa-solid fa-circle-exclamation"
          class="text-danger h-4.5 w-4.5 mr-1"
        />
        {{ unavailableUnitsStr }}
      </p>
    </div>

    <!-- ADD COMMENT -->
    <div
      class="w-full flex flex-col justify-start transform transition-all duration-500 ease-out absolute top-0 left-0 pt-14"
      :class="stage === 0 && 'translate-x-full opacity-0'"
      :aria-hidden="stage === 0"
      :tabindex="stage === 0 ? -1 : null"
    >
      <div class="w-full mb-7 sm:w-auto text-left text-gray-800 dark:text-white/80">
        <p>
          You are about to
          <span class="italic">{{ addedValue >= 0 ? ' add ' : ' consume ' }}</span>
          <span class="font-medium text-primary-600 dark:text-primary-200"> {{ displayNum(Math.abs(addedValue)) }}
            <span v-if="selUnitAbbreviation">
              <component
                v-for="part in selUnitAbbreviation"
                :is="part.type ? part.type : 'span'"
                :key="part.key"
              >
                {{ part.content }}
              </component>
            </span>
          </span>
          {{ addedValue >= 0 ? 'to' : 'from' }}
          <span class="font-medium text-primary-600 dark:text-primary-200">{{ getProceedArgs.part_number }}</span>'s "On Hand" count.
        </p>
        <p class="mt-2">
          Please record any comments below.
        </p>
      </div>
      <div class="w-full">
        <textarea
          v-model="comment"
          class="w-full max-h-32 border rounded mt-2 p-2 border-gray-300 dark:border-gray-800 focus:ring-1 focus:ring-primary focus:border focus:border-primary"
          rows="3"
          :disabled="stage === 0"
        />
      </div>
      <p
        v-if="showSubmitError"
        class="w-11/12 sm:w-9/12 md:w-7/12 lg:w-80 text-left text-sm text-danger font-medium mt-2"
      >
        <font-awesome-icon
          icon="fa-solid fa-circle-exclamation"
          class="text-danger h-4.5 w-4.5 mr-1"
        />
        Unable to submit change.
      </p>
    </div>


    <section class="w-full flex flex-wrap gap-y-2 gap-x-2 mt-6 sm:flex-nowrap">
      <VButton
        :label="stage === 0 ? 'Cancel' : 'Back'"
        @click="goBack"
        size="sm"
        :icon="stage === 0 ? '' : 'fa-solid fa-arrow-left'"
        icon-size="sm"
        icon-first
        uppercase
        color="secondary"
        outlined
        class="w-full sm:w-1/2"
      />
      <VButton
        :label="stage === 0 ? 'Next' : (submitting ? 'Submitting...' : 'Submit')"
        @click="submit"
        size="sm"
        :disabled="submitting || !addedValue"
        :submitting-prepend="submitting"
        uppercase
        :icon="stage === 0 ? 'fa-solid fa-arrow-right' : ''"
        icon-size="sm"
        color="primary"
        class="w-full sm:w-1/2"
      />
    </section>

  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex"
import NumberInput from "@/components/Inputs/NumberInput/NumberInput.vue"
import VButton from "@/components/Buttons/Button/VButton.vue"
import { postOnHandRecordCreate } from "../../../services/Inventory"

export default {
  components: {
    NumberInput,
    VButton
  },
  data() {
    return {
      stage: 0,
      addedValue: null,
      selUnit: "part_number",
      comment: "",
      submitting: false,
      showSubmitError: false,
      shadowClass: "",
      ripple: {
        active: false,
        x: 0,
        y: 0,
        color: 'transparent',
        transform: 'translate(-50%, -50%) scale(0)',
        opacity: 0,
      },
      rippleTimeout: null,
    }
  },
  computed: {
    ...mapState(["headers"]),
    ...mapGetters([
      "getProceedArgs",
      "getUnits"
    ]),
    onHandVal() {
      return this.getProceedArgs?.onHandVal
    },
    convertedOnHandVal() {
      if (this.selUnit === "on_hand") return parseFloat(this.onHandVal)
      return parseFloat(this.onHandVal) * this.conversions.on_hand_unit?.[this.selUnitKey]?.ratio_target_to_source
    },
    changeValConvertedToOnHandUnit() {
      if (this.selUnit === "on_hand") return this.addedValue
      return this.addedValue * this.conversions[this.selUnitKey]?.on_hand_unit?.ratio_target_to_source
    },
    convertedTotalVal() {
      return this.convertedOnHandVal + this.addedValue
    },
    units() {
      return this.getProceedArgs?.units
    },
    conversions() {
      return this.getProceedArgs?.conversions
    },
    filteredUnits() {
      const priorityOrder = ["on_hand", "part_number", "consumed",]
      const seenIds = new Set()
      const result = {
        available: {},
        conversionError: {}
      }

      for (const key of priorityOrder) {
        const unit = this.units[key]
        if (unit && !seenIds.has(unit.id)) {
          if (key !== "on_hand" && !this.conversions.on_hand_unit[`${key}_unit`]) {
            result.conversionError[key] = unit
            continue
          }
          else {
            result.available[key] = unit
            seenIds.add(unit.id)
          }
        }
      }

      return result
    },
    availableUnits() {
      return this.filteredUnits.available
    },
    unitsWithConversionError() {
      return this.filteredUnits.conversionError
    },
    unavailableUnitsStr() {
      let unavailableKeys = Object.keys(this.unitsWithConversionError)
      if (unavailableKeys.length === 0) return null
      return `${unavailableKeys.map(x => this.capitalize(x).replace("_", " ")).join(" and ")} Unit${unavailableKeys.length === 1 ? '' : 's'} ${unavailableKeys.length === 1 ? 'is' : 'are'} unavailable for selection as ${unavailableKeys.length === 1 ? 'it' : 'they'} cannot be converted from the On Hand Unit.`
    },
    selUnitKey() {
      return `${this.selUnit}_unit`
    },
    selUnitAbbreviation() {
      return this.displayAbbr(this.getUnits[this.units[this.selUnit].id].abbreviation)
    },
    partNumber() {
      return this.getProceedArgs?.part_number
    },
    rippleStyle() {
      return {
        width: '100px',
        height: '100px',
        left: `${this.ripple.x}px`,
        top: `${this.ripple.y}px`,
        transform: this.ripple.transform,
        background: `radial-gradient(circle, ${this.ripple.color} 0%, transparent 100%)`,
        opacity: this.ripple.opacity
      }
    }
  },
  watch: {
    addedValue(val, oldVal) {
      if (val > 0 && oldVal <= 0) this.triggerBoxShadowEffect('boxShadow-green')
      else if (val < 0 && oldVal >= 0) this.triggerBoxShadowEffect('boxShadow-red')
    }
  },
  created() {
    this.editValue = this.onHandVal
    if (this.units.on_hand.id === this.units.part_number.id) this.selUnit = "on_hand"
  },
  methods: {
    ...mapMutations([
      "setProceedArgs",
      "closePrompt",
      "proceedPrompt",
      "refreshData",
      "setPrompt"
    ]),
    setValue(value) {
      this.addedValue = value
    },
    incrementAdd() {
      this.addedValue++
      this.triggerRipple("+")
    },
    decrementAdd() {
      this.addedValue--
      this.triggerRipple("-")
    },
    selectUnit(type) {
      this.selUnit = type
    },
    goBack() {
      if (this.stage === 0) this.closePrompt()
      else this.stage--
      this.showSubmitError = false
    },
    async submit() {
      if (this.stage === 0) this.stage ++
      else this.submitOnHandRecord()
    },
    async submitOnHandRecord() {
      this.submitting = true
      let requestBody = {
        inventory_item_id: this.getProceedArgs?.itemId,
        change: this.changeValConvertedToOnHandUnit,
        site_employee_id: this.headers.siteEmployeeId,
        site_id: this.headers.siteId,
        comment: this.comment,
        transaction_type: "transaction",
        unit_of_measure: this.units.on_hand.id
      }
      postOnHandRecordCreate(requestBody, this.headers)
      .then(async (response) => {
        if (response?.status === 200) {
          this.refreshData(true)
          this.closePrompt()
          this.submitting = false
        } else {
          console.log(response)
          this.showSubmitError = true
          this.submitting = false
          if (response.status === 403) {
            this.setPrompt(["You do not have the required permissions for this action.", "closePrompt", null, { header: "Error" }])
          }
        }
      }, (error) => {
        console.log(error)
        this.showSubmitError = true
        this.submitting = false
      })
    },
    displayNum(str) {
      let num = parseFloat(str)
      if (isNaN(num)) return "0.000"
      if (num < 0.01) return num
      return new Intl.NumberFormat("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(parseFloat(num))
    },
    displayAbbr(abbreviation) {
      if (!abbreviation) return
      return abbreviation.split(/(\^\w+)/g).map((part, index) => {
        if (part.startsWith('^')) {
          return { type: 'sup', content: part.slice(1), key: `sup-${index}` }
        }
        return { type: 'text', content: part, key: `text-${index}` }
      })
    },
    triggerRipple(val) {
      // Clear any ongoing ripple animation
      if (this.rippleTimeout) {
        clearTimeout(this.rippleTimeout)
      }

      // Temporarily reset ripple state to avoid horizontal translation
      this.ripple.transform = 'translate(-50%, -50%) scale(0)'
      this.ripple.opacity = 0

      const buttonRect = event.target.getBoundingClientRect()
      const containerRect = this.$refs.container.getBoundingClientRect()

      // Trigger reflow to apply the reset state
      void this.$refs.container.offsetHeight

      this.ripple.x = buttonRect.left - containerRect.left + buttonRect.width / 2
      this.ripple.y = buttonRect.top - containerRect.top + buttonRect.height / 2
      this.ripple.color = val === '+' ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255, 0, 0, 0.2)' // Green or red
      this.ripple.opacity = 1

      // Trigger animation
      this.ripple.transform = 'translate(-50%, -50%) scale(2)'

      setTimeout(() => {
        this.ripple.opacity = 0 // Trigger fade-out by setting opacity to 0
        this.ripple.transform = 'translate(-50%, -50%) scale(0)'
      }, 175)
    },
    triggerBoxShadowEffect(className) {
      this.shadowClass = className
      setTimeout(() => {
        this.shadowClass = '' // Remove class to reset animation
      }, 500) // Match the duration of the CSS animation
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
}
</script>

<style scoped>
.editPrompt {
  min-width: 18rem;
}
button {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
@keyframes boxShadow-green {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 128, 0, 0.3); /* Initial green glow */
  }
  100% {
    box-shadow: 0 0 9px 9px rgba(0, 128, 0, 0); /* Fade out */
  }
}

@keyframes boxShadow-red {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.3); /* Initial red glow */
  }
  100% {
    box-shadow: 0 0 9px 9px rgba(255, 0, 0, 0); /* Fade out */
  }
}

.boxShadow-green {
  animation: boxShadow-green 0.5s ease-out;
}

.boxShadow-red {
  animation: boxShadow-red 0.5s ease-out;
}

</style>